import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AppWindow, Plus, Pencil, Trash2, Check, User, Layers } from "lucide-react";

const EMPTY = { name: "", owner: "", description: "", groups: [] };

// Small kind badge so admins can tell group types apart in the picker.
const GROUP_KIND = {
  licensing: ["apps.kindLicensing", "border-amber-200 bg-amber-50 text-amber-700"],
  cloud_teams: ["apps.kindTeams", "border-violet-200 bg-violet-50 text-violet-700"],
  cloud_distribution: ["apps.kindDL", "border-sky-200 bg-sky-50 text-sky-700"],
  m365_group: ["apps.kindM365", "border-teal-200 bg-teal-50 text-teal-700"],
};
function groupKind(cls) {
  return GROUP_KIND[cls] || ["apps.kindSecurity", "border-zinc-200 bg-zinc-100 text-zinc-600"];
}
function KindBadge({ cls, on }) {
  const { t } = useLang();
  const [key, style] = groupKind(cls);
  return (
    <span className={`ml-1 rounded-sm border px-1 py-0 text-[9px] font-bold uppercase tracking-wider ${on ? "border-white/40 bg-white/20 text-white" : style}`}>
      {t(key)}
    </span>
  );
}


export default function Applications() {
  const { can } = useAuth();
  const isManager = can("applications.manage");
  const { t } = useLang();
  const [apps, setApps] = useState([]);
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmDel, setConfirmDel] = useState(null);
  const [search, setSearch] = useState("");

  const load = () => Promise.all([api.get("/applications"), api.get("/groups")])
    .then(([a, g]) => { setApps(a.data); setGroups(g.data); });
  useEffect(() => { load(); }, []);

  const groupMap = useMemo(() => Object.fromEntries(groups.map((g) => [g.id, g])), [groups]);
  // Assignable = every kind of group (security, distribution, M365, licensing, Teams), excluding only
  // dynamic (automatic) membership groups — a user cannot be added to those manually.
  const assignable = useMemo(() => groups.filter((g) => !g.dynamicMembership), [groups]);
  // Available to bind = unbound, or already bound to the application being edited.
  const available = useMemo(() => assignable.filter(
    (g) => (!g.applicationId || g.applicationId === editing?.id) &&
      g.displayName.toLowerCase().includes(search.toLowerCase())), [assignable, editing, search]);

  const startNew = () => { setEditing(null); setForm(EMPTY); setSearch(""); setOpen(true); };
  const startEdit = (a) => { setEditing(a); setForm({ ...EMPTY, ...a }); setSearch(""); setOpen(true); };
  const toggle = (id) => setForm((p) => ({ ...p, groups: p.groups.includes(id) ? p.groups.filter((x) => x !== id) : [...p.groups, id] }));

  const save = async () => {
    if (!form.name.trim()) { toast.error(t("apps.nameRequired")); return; }
    try {
      if (editing) await api.put(`/applications/${editing.id}`, form);
      else await api.post("/applications", form);
      toast.success(editing ? t("apps.updated") : t("apps.created"));
      setOpen(false); await load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async () => {
    try { await api.delete(`/applications/${confirmDel.id}`); toast.success(t("apps.deleted")); setConfirmDel(null); await load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <>
      <PageHeader title={t("apps.title")} subtitle={t("apps.subtitle")}>
        {isManager && (
          <Button onClick={startNew} data-testid="new-application" className="rounded-sm bg-primary hover:bg-[#002480]">
            <Plus size={16} className="mr-2" /> {t("apps.new")}
          </Button>
        )}
      </PageHeader>

      <div className="p-6 md:p-10">
        {apps.length === 0 ? (
          <div className="flex flex-col items-center justify-center border border-dashed border-zinc-300 bg-white py-20 text-center" data-testid="apps-empty">
            <AppWindow size={32} className="text-zinc-300" />
            <p className="mt-3 text-sm text-zinc-500">{t("apps.empty")}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-px bg-zinc-200 lg:grid-cols-2">
            {apps.map((a) => (
              <div key={a.id} className="bg-white p-6" data-testid={`application-card-${a.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center bg-primary/10 text-primary"><AppWindow size={18} /></div>
                    <div>
                      <div className="text-sm font-bold text-zinc-900">{a.name}</div>
                      {a.owner && <div className="flex items-center gap-1 text-xs text-zinc-500"><User size={11} /> {a.owner}</div>}
                    </div>
                  </div>
                  {isManager && (
                    <div className="flex gap-1">
                      <button onClick={() => startEdit(a)} data-testid={`edit-application-${a.id}`} className="p-1.5 text-zinc-400 hover:bg-zinc-100 hover:text-primary"><Pencil size={15} /></button>
                      <button onClick={() => setConfirmDel(a)} data-testid={`delete-application-${a.id}`} className="p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
                    </div>
                  )}
                </div>
                {a.description && <p className="mt-3 text-sm text-zinc-500">{a.description}</p>}
                <div className="mt-4 border-t border-zinc-100 pt-3">
                  <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-400"><Layers size={12} /> {t("apps.groups")} ({a.groups.length})</div>
                  <div className="flex flex-wrap gap-1.5">
                    {a.groups.length === 0 ? <span className="text-sm text-zinc-300">{t("common.none")}</span> :
                      a.groups.map((gid) => (
                        <span key={gid} className="inline-flex items-center border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-xs text-zinc-600">{groupMap[gid]?.displayName || gid}{groupMap[gid] && <KindBadge cls={groupMap[gid].classification} />}</span>
                      ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto rounded-sm sm:max-w-2xl" data-testid="application-form-dialog">
          <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">{editing ? t("apps.editTitle") : t("apps.newTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-5 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("apps.name")} *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="application-name" className="rounded-sm border-zinc-300" /></div>
              <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("apps.owner")}</Label>
                <Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} data-testid="application-owner" className="rounded-sm border-zinc-300" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">{t("apps.desc")}</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="application-desc" className="rounded-sm border-zinc-300" /></div>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-500"><Layers size={13} /> {t("apps.assignGroups")} ({form.groups.length})</Label>
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("common.search")} data-testid="application-group-search" className="h-8 w-44 rounded-sm border-zinc-300 text-xs" />
              </div>
              <p className="mb-2 text-[11px] text-zinc-400">{t("apps.assignHint")}</p>
              <div className="flex max-h-64 flex-wrap gap-2 overflow-y-auto border border-zinc-100 p-2">
                {available.length === 0 ? <span className="px-1 py-2 text-sm text-zinc-400">{t("apps.noGroups")}</span> :
                  available.map((g) => {
                    const on = form.groups.includes(g.id);
                    return (
                      <button key={g.id} type="button" onClick={() => toggle(g.id)} data-testid={`app-group-${g.id}`}
                        className={`flex items-center gap-1 border px-2.5 py-1 text-xs ${on ? "border-primary bg-primary text-white" : "border-zinc-300 bg-white text-zinc-600 hover:border-zinc-400"}`}>
                        {on && <Check size={12} />} {g.displayName}<KindBadge cls={g.classification} on={on} />
                      </button>
                    );
                  })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-sm">{t("common.cancel")}</Button>
            <Button onClick={save} data-testid="save-application" className="rounded-sm bg-primary hover:bg-[#002480]">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <DialogContent className="rounded-sm sm:max-w-md" data-testid="application-delete-dialog">
          <DialogHeader><DialogTitle className="font-display uppercase tracking-tight">{t("apps.deleteTitle")}</DialogTitle></DialogHeader>
          <p className="py-2 text-sm text-zinc-600">{t("apps.deleteConfirm")} <span className="font-semibold">{confirmDel?.name}</span>?</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDel(null)} className="rounded-sm">{t("common.cancel")}</Button>
            <Button onClick={del} data-testid="application-delete-confirm" className="rounded-sm bg-red-600 hover:bg-red-700">{t("common.delete")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
